// User API Client - Communicates with user-service via API Gateway

import type {
  UserDirectoryRecord,
  CreateUserRequest,
  UpdateUserRequest,
  PasswordUpdateRequest,
  UserListFilters,
  UserListResponse
} from '@/types/user-directory';
import { fetchWithAuth } from './http-client';
import { parseStandardApiResponse, ApiError } from './standard-api';

const MAX_USER_PAGE_SIZE = 100;

/**
 * Maps API snake_case response to camelCase for frontend consumption
 */
function mapUserFromApi(apiUser: any): UserDirectoryRecord {
  return {
    id: apiUser.id,
    code: apiUser.code,
    username: apiUser.username,
    email: apiUser.email,
    fullName: apiUser.full_name,
    jobTitle: apiUser.job_title,
    phoneNumber: apiUser.phone_number,
    avatarUrl: apiUser.avatar_url,
    avatarBase64: apiUser.avatar_base64,
    countryCode: apiUser.country_code,
    countryId: apiUser.country_id,
    stateId: apiUser.state_id,
    addressLine1: apiUser.address_line1,
    addressLine2: apiUser.address_line2,
    city: apiUser.city,
    postalCode: apiUser.postal_code,
    region: apiUser.region,
    linkedinUrl: apiUser.linkedin_url,
    nationality: apiUser.nationality,
    role: apiUser.role,
    authProvider: apiUser.auth_provider,
    statusId: apiUser.status_id,
    departmentId: apiUser.department_id,
    employeeTypeId: apiUser.employee_type_id,
    managerId: apiUser.manager_id,
    businessUnitId: apiUser.business_unit_id,
    divisionId: apiUser.division_id,
    officeLocationId: apiUser.office_location_id,
    costCenterId: apiUser.cost_center_id,
    mfaEnabled: apiUser.mfa_enabled,
    lastLogin: apiUser.last_login,
    isActive: apiUser.is_active,
    createdAt: apiUser.created_at,
    updatedAt: apiUser.updated_at,
    employeeId: apiUser.employee_id,
    hireDate: apiUser.hire_date,
    leaveDate: apiUser.leave_date,
    deletedAt: apiUser.deleted_at,
    notes: apiUser.notes,
    // Related entities (also need mapping)
    manager: apiUser.manager ? {
      id: apiUser.manager.id,
      username: apiUser.manager.username,
      fullName: apiUser.manager.full_name,
      email: apiUser.manager.email,
    } : undefined,
    // status is expected to be a string (UserStatus type), derive from user_status.code or fallback
    status: apiUser.user_status?.code || apiUser.status || 'ACTIVE',
    // userStatus is the full object for detailed display
    userStatus: apiUser.user_status ? {
      id: apiUser.user_status.id,
      code: apiUser.user_status.code,
      name: apiUser.user_status.name,
      colorCode: apiUser.user_status.color_code,
      allowsLogin: apiUser.user_status.allows_login,
    } : undefined,
    department: apiUser.department ? {
      id: apiUser.department.id,
      code: apiUser.department.code,
      name: apiUser.department.name,
      category: apiUser.department.category,
    } : undefined,
    employeeType: apiUser.employee_type ? {
      id: apiUser.employee_type.id,
      code: apiUser.employee_type.code,
      name: apiUser.employee_type.name,
      category: apiUser.employee_type.category,
    } : undefined,
    businessUnit: apiUser.business_unit ? {
      id: apiUser.business_unit.id,
      code: apiUser.business_unit.code,
      name: apiUser.business_unit.name,
    } : null,
    division: apiUser.division ? {
      id: apiUser.division.id,
      code: apiUser.division.code,
      name: apiUser.division.name,
    } : null,
    officeLocation: apiUser.office_location ? {
      id: apiUser.office_location.id,
      code: apiUser.office_location.code,
      name: apiUser.office_location.name,
    } : null,
    costCenter: apiUser.cost_center ? {
      id: apiUser.cost_center.id,
      code: apiUser.cost_center.code,
      name: apiUser.cost_center.name,
    } : null,
    teams: apiUser.teams?.map((tm: any) => ({
      teamId: tm.team_id,
      roleInTeam: tm.role_in_team,
      joinedAt: tm.joined_at,
      team: tm.team ? {
        id: tm.team.id,
        code: tm.team.code,
        name: tm.team.name,
      } : null,
    })) ?? [],
    // PROJ-032: Invitation status information
    invitationInfo: apiUser.invitation_info ? {
      hasActiveInvitation: apiUser.invitation_info.has_active_invitation,
      expiresAt: apiUser.invitation_info.expires_at,
      createdAt: apiUser.invitation_info.created_at,
      hoursRemaining: apiUser.invitation_info.hours_remaining,
    } : null,
  };
}

// Route through API Gateway by default; Vite dev server proxies '/api' -> 3101
// Using versioned API endpoints (v1) - these have proper camelCase/snake_case transformations
const RAW_USER_API_URL = import.meta.env.VITE_USER_API_URL || '';

function normalizeUserApiBaseUrl(url: string): string {
  const trimmed = url.trim().replace(/\/+$/, '');

  if (!trimmed) {
    return '/api/v1/users';
  }

  if (trimmed.endsWith('/api/v1/users')) {
    return trimmed;
  }

  if (trimmed.endsWith('/api/users')) {
    return `${trimmed.slice(0, -('/api/users'.length))}/api/v1/users`;
  }

  if (trimmed.endsWith('/api/v1')) {
    return `${trimmed}/users`;
  }

  if (trimmed.endsWith('/api')) {
    return `${trimmed}/v1/users`;
  }

  return `${trimmed}/api/v1/users`;
}

const USER_API_BASE_URL = normalizeUserApiBaseUrl(RAW_USER_API_URL);
const USER_API_URL = USER_API_BASE_URL;
const USER_API_ENV_VAR = 'VITE_USER_API_URL';

// ============================================
// Authentication API (de ahora proviene de auth.ts)
// ============================================

export { 
  getAccessToken, 
  setAccessToken, 
  getRefreshToken, 
  setRefreshToken, 
  clearTokens, 
  cacheCurrentUser, 
  getCachedUser, 
  clearCachedUser,
  login,
  register,
  logout,
  getCurrentUser  // La función más crítica ahora sí está exportada
} from './auth';

// ============================================
// User CRUD API
// ============================================

export async function listUsers(filters: UserListFilters = {}): Promise<UserListResponse> {
  const params = new URLSearchParams();

  if (filters.page) params.set('page', filters.page.toString());

  if (typeof filters.limit === 'number') {
    const safeLimit = Math.min(filters.limit, MAX_USER_PAGE_SIZE);
    params.set('limit', safeLimit.toString());
  }
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.role) params.set('role', filters.role);
  if (filters.managerId) params.set('manager_id', filters.managerId);
  if (filters.includeDeleted) params.set('include_deleted', 'true');

  const response = await fetchWithAuth(`${USER_API_URL}?${params.toString()}`, {
    context: 'Listado de usuarios',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  const { data: users, meta } = await parseStandardApiResponse<Array<UserDirectoryRecord>>(
    response,
    'Listado de usuarios'
  );

  // Map snake_case API response to camelCase for frontend
  const mappedUsers = (users ?? []).map(mapUserFromApi);

  const paginationMeta = meta.pagination ?? {
    page: Number(filters.page ?? 1),
    limit: Number(filters.limit ?? (mappedUsers?.length || 1)),
    total: mappedUsers?.length ?? 0,
    totalPages: mappedUsers?.length && filters.limit ? Math.ceil(mappedUsers.length / filters.limit) : 1
  };

  return {
    users: mappedUsers,
    pagination: {
      page: Number(paginationMeta.page ?? filters.page ?? 1),
      limit: Number(paginationMeta.limit ?? filters.limit ?? (users?.length || 1)),
      total: Number(paginationMeta.total ?? users?.length ?? 0),
      pages: Number(paginationMeta.totalPages ?? (Math.ceil((paginationMeta.total ?? users?.length ?? 0) / (paginationMeta.limit || 1)) || 1))
    }
  };
}

type ListAllUsersOptions = Omit<UserListFilters, 'page' | 'limit'> & {
  chunkSize?: number;
};

export async function listAllUsers(options: ListAllUsersOptions = {}): Promise<UserDirectoryRecord[]> {
  const { chunkSize, ...filters } = options;
  const pageSize = Math.min(chunkSize ?? MAX_USER_PAGE_SIZE, MAX_USER_PAGE_SIZE);

  const allUsers: UserDirectoryRecord[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;
  let totalPages = 1;

  do {
    const { users, pagination } = await listUsers({ ...filters, page, limit: pageSize });
    allUsers.push(...users);
    total = pagination.total ?? allUsers.length;
    totalPages = pagination.pages || Math.max(1, Math.ceil(total / pageSize));
    page += 1;
  } while (page <= totalPages && allUsers.length < total);

  return allUsers;
}

export async function getUserById(id: string): Promise<UserDirectoryRecord> {
  const response = await fetchWithAuth(`${USER_API_URL}/${id}`, {
    context: 'Detalle de usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });

  const { data: user } = await parseStandardApiResponse<any>(
    response,
    'Detalle de usuario'
  );
  if (!user) {
    throw new ApiError('User not found', { status: 404, code: 'NOT_FOUND' });
  }
  // Map snake_case API response to camelCase
  return mapUserFromApi(user);
}

export async function fetchUserByEmail(email: string): Promise<UserDirectoryRecord | null> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return null;

  const response = await listUsers({ search: normalizedEmail, limit: 5 });

  const match = response.users.find(
    (user) => user.email.toLowerCase() === normalizedEmail
  );

  return match || null;
}

export async function createUser(userData: CreateUserRequest): Promise<UserDirectoryRecord> {
  // Ensure username mirrors email if not explicitly provided
  const normalized: any = {
    ...userData,
    username: (userData as any).username || userData.email
  };
  const response = await fetchWithAuth(`${USER_API_URL}`, {
    method: 'POST',
    body: JSON.stringify(normalized),
    context: 'Creación de usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  const { data: user } = await parseStandardApiResponse<UserDirectoryRecord>(
    response,
    'Creación de usuario'
  );
  // Backend already transforms to camelCase via transformUserRecord()
  return user;
}

export async function updateUser(id: string, userData: UpdateUserRequest): Promise<UserDirectoryRecord> {
  const response = await fetchWithAuth(`${USER_API_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(userData),
    context: 'Actualización de usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  const { data: user } = await parseStandardApiResponse<UserDirectoryRecord>(
    response,
    'Actualización de usuario'
  );
  // Backend already transforms to camelCase via transformUserRecord()
  return user;
}

// ================================
// Helpers
// ================================

/**
 * NOTE: No transformation helpers needed!
 *
 * The backend User Service uses Prisma with transformUserRecord() which already
 * converts all snake_case database fields to camelCase before sending to frontend.
 *
 * See: services/user-service/src/services/userService.js - transformUserRecord()
 *
 * Fields arrive as: fullName, jobTitle, phoneNumber, avatarUrl, authProvider,
 * managerId, businessUnitId, statusId, departmentId, employeeTypeId, etc.
 */

export async function deleteUser(id: string): Promise<void> {
  const response = await fetchWithAuth(`${USER_API_URL}/${id}`, {
    method: 'DELETE',
    context: 'Eliminación de usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  await parseStandardApiResponse<unknown>(response, 'Eliminación de usuario');
}

/**
 * Bulk delete multiple users at once
 * @param ids - Array of user IDs to delete
 * @returns Result with deleted count and any failed IDs
 */
export async function bulkDeleteUsers(ids: string[]): Promise<{ deleted: number; failed: string[]; total: number }> {
  if (!ids || ids.length === 0) {
    return { deleted: 0, failed: [], total: 0 };
  }

  const response = await fetchWithAuth(`${USER_API_URL}/bulk`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
    context: 'Eliminación masiva de usuarios',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });

  const { data } = await parseStandardApiResponse<{ deleted: number; failed: string[]; total: number }>(
    response,
    'Eliminación masiva de usuarios'
  );

  return data ?? { deleted: 0, failed: ids, total: ids.length };
}

export async function restoreUser(id: string): Promise<UserDirectoryRecord> {
  const response = await fetchWithAuth(`${USER_API_URL}/${id}/restore`, {
    method: 'POST',
    context: 'Restauración de usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  const { data: user } = await parseStandardApiResponse<UserDirectoryRecord>(
    response,
    'Restauración de usuario'
  );
  // Backend already transforms to camelCase via transformUserRecord()
  return user;
}

export async function updatePassword(userId: string, passwords: PasswordUpdateRequest): Promise<void> {
  const response = await fetchWithAuth(`${USER_API_URL}/${userId}/password`, {
    method: 'POST',
    body: JSON.stringify({
      current_password: passwords.currentPassword,
      new_password: passwords.newPassword,
    }),
    context: 'Actualización de contraseña',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  await parseStandardApiResponse<unknown>(response, 'Actualización de contraseña');
}

export async function resetPassword(
  userId: string,
  mode: 'auto' | 'manual' = 'auto',
  password?: string
): Promise<{ message: string; tempPassword?: string; mode: string }> {
  const response = await fetchWithAuth(`${USER_API_URL}/${userId}/password-reset`, {
    method: 'POST',
    context: 'Reseteo de contraseña',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
    body: JSON.stringify({ mode, password }),
  });
  const { data } = await parseStandardApiResponse<{ message: string; tempPassword?: string; mode: string }>(
    response,
    'Reseteo de contraseña'
  );
  if (!data) {
    throw new ApiError('Respuesta inválida del servicio de usuarios', { status: 500 });
  }

  return data;
}

/**
 * Change current user's password
 * @param currentPassword - User's current password
 * @param newPassword - New password to set
 * @returns Success message
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ message: string }> {
  const response = await fetchWithAuth(`${USER_API_URL}/me/change-password`, {
    method: 'POST',
    context: 'Cambio de contraseña',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const { data } = await parseStandardApiResponse<{ message: string }>(
    response,
    'Cambio de contraseña'
  );
  if (!data) {
    throw new ApiError('Respuesta inválida del servicio de usuarios', { status: 500 });
  }

  return data;
}

/**
 * Archive user
 * @param userId - User ID to archive
 * @param reason - Optional reason for archiving
 * @returns Updated user record
 */
export async function archiveUser(userId: string, reason?: string): Promise<UserDirectoryRecord> {
  const response = await fetchWithAuth(`${USER_API_URL}/${userId}/archive`, {
    method: 'PATCH',
    body: JSON.stringify({ reason }),
    context: 'Archivar usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  const { data: user } = await parseStandardApiResponse<UserDirectoryRecord>(
    response,
    'Archivar usuario'
  );
  return user;
}

/**
 * Unarchive user
 * @param userId - User ID to unarchive
 * @returns Updated user record
 */
export async function unarchiveUser(userId: string): Promise<UserDirectoryRecord> {
  const response = await fetchWithAuth(`${USER_API_URL}/${userId}/unarchive`, {
    method: 'PATCH',
    context: 'Desarchivar usuario',
    envVar: USER_API_ENV_VAR,
    baseUrl: USER_API_BASE_URL,
  });
  const { data: user } = await parseStandardApiResponse<UserDirectoryRecord>(
    response,
    'Desarchivar usuario'
  );
  return user;
}
